import { ReactNode, useEffect, useMemo, useState } from "react";
import { MODERN_BROWSER_POLICY } from "@shared/config/browserSupport";
import {
  BrowserGateBranding,
} from "@shared/components/BrowserSupportGate";
import {
  detectBrowser,
  evaluateBrowserSupport,
  shouldBypassBrowserGate,
} from "@shared/utils/browserDetection";
import BrowserSupportGate from "@shared/components/BrowserSupportGate";
import { trackLoginFunnelEvent } from "@shared/services/loginFunnelTelemetry";
import {
  getOrganizationLookupInputFromHostname,
  getOrganizationSlugFromHostname,
} from "@shared/utils/orgLoginRouting";
import {
  resolveOrgForLogin,
  ResolveOrgRateLimitError,
} from "@shared/services/resolveOrgClient";
import { getProductBrand } from "@shared/config/productBrand";

const MODERN_BROWSER_OVERRIDE_KEY = "modern_browser_override";
const MODERN_BROWSER_WARNING_ACK_KEY = "modern_browser_warning_ack";
const DEFAULT_PLATFORM_NAME = getProductBrand().clinicalPlatformName;

type BrowserGateStorageRecord = {
  browserKey: string;
  acknowledgedAt: string;
  expiresAt?: string;
};

type RequireModernBrowserProps = {
  children: ReactNode;
};

function toTitleCaseFromSlug(slug: string): string {
  return slug
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function safeParseStorageRecord(raw: string | null): BrowserGateStorageRecord | null {
  if (!raw || raw === "1") return null;
  try {
    const parsed = JSON.parse(raw) as BrowserGateStorageRecord;
    if (!parsed || typeof parsed.browserKey !== "string") return null;
    return parsed;
  } catch {
    return null;
  }
}

function isRecordActive(
  record: BrowserGateStorageRecord | null,
  browserKey: string,
): boolean {
  if (!record) return false;
  if (record.browserKey !== browserKey) return false;
  if (!record.expiresAt) return true;
  const expiresAtMs = new Date(record.expiresAt).getTime();
  return Number.isFinite(expiresAtMs) && expiresAtMs > Date.now();
}

export default function RequireModernBrowser({
  children,
}: RequireModernBrowserProps) {
  const browser = useMemo(() => detectBrowser(), []);
  const evaluation = useMemo(
    () => evaluateBrowserSupport(browser, MODERN_BROWSER_POLICY),
    [browser],
  );
  const browserPolicyKey = useMemo(() => {
    return [
      MODERN_BROWSER_POLICY.policyVersion || "browser-policy-v1",
      browser.family,
      browser.major ?? "unknown",
      evaluation.minimumVersion ?? "none",
      evaluation.warningVersion ?? "none",
      evaluation.state,
    ].join(":");
  }, [
    browser.family,
    browser.major,
    evaluation.minimumVersion,
    evaluation.warningVersion,
    evaluation.state,
  ]);

  const bypassBrowserGate = useMemo(
    () => shouldBypassBrowserGate(window.location, browser.userAgent),
    [browser.userAgent],
  );
  const [overrideEnabled, setOverrideEnabled] = useState<boolean>(
    () => bypassBrowserGate,
  );
  const [warningAcknowledged, setWarningAcknowledged] = useState<boolean>(
    () => bypassBrowserGate,
  );
  const [branding, setBranding] = useState<BrowserGateBranding>({
    orgName: DEFAULT_PLATFORM_NAME,
    orgSlug: null,
    logoUrl: null,
    source: "default",
  });

  useEffect(() => {
    if (shouldBypassBrowserGate(window.location, browser.userAgent)) {
      setOverrideEnabled(true);
      setWarningAcknowledged(true);
      return;
    }

    try {
      const overrideRaw = window.sessionStorage.getItem(
        MODERN_BROWSER_OVERRIDE_KEY,
      );
      if (overrideRaw === "1") {
        setOverrideEnabled(true);
      } else {
        const overrideRecord = safeParseStorageRecord(overrideRaw);
        setOverrideEnabled(isRecordActive(overrideRecord, browserPolicyKey));
      }

      const warningRaw = window.sessionStorage.getItem(
        MODERN_BROWSER_WARNING_ACK_KEY,
      );
      const warningRecord = safeParseStorageRecord(warningRaw);
      setWarningAcknowledged(isRecordActive(warningRecord, browserPolicyKey));
    } catch {
      setOverrideEnabled(false);
      setWarningAcknowledged(false);
    }
  }, [browser.userAgent, browserPolicyKey]);

  useEffect(() => {
    if (evaluation.state !== "blocked" || overrideEnabled) return;
    void trackLoginFunnelEvent({
      stage: "browser_gate_blocked",
      policy_state: evaluation.state,
      policy_version: MODERN_BROWSER_POLICY.policyVersion,
      reachability_status: "unknown",
    });
  }, [evaluation.state, overrideEnabled]);

  useEffect(() => {
    if (evaluation.state !== "warning" || warningAcknowledged) return;
    void trackLoginFunnelEvent({
      stage: "browser_gate_warning",
      policy_state: evaluation.state,
      policy_version: MODERN_BROWSER_POLICY.policyVersion,
      warning_acknowledged: false,
    });
  }, [evaluation.state, warningAcknowledged]);

  useEffect(() => {
    const lookupInput = getOrganizationLookupInputFromHostname(
      window.location.hostname,
    );
    const fallbackSlug = getOrganizationSlugFromHostname(
      window.location.hostname,
    );
    if (!lookupInput) {
      setBranding({
        orgName: DEFAULT_PLATFORM_NAME,
        orgSlug: null,
        logoUrl: null,
        source: "default",
      });
      return;
    }

    setBranding({
      orgName: fallbackSlug
        ? toTitleCaseFromSlug(fallbackSlug)
        : DEFAULT_PLATFORM_NAME,
      orgSlug: fallbackSlug,
      logoUrl: null,
      source: fallbackSlug ? "slug_fallback" : "default",
    });

    let isActive = true;
    const resolveBranding = async () => {
      try {
        const payload = await resolveOrgForLogin(lookupInput, {
          cacheTtlMs: 60_000,
        });
        const organization = payload?.organization;
        if (!organization) return;

        const name =
          typeof organization["name"] === "string" &&
          organization["name"].trim().length > 0
            ? organization["name"].trim()
            : fallbackSlug
              ? toTitleCaseFromSlug(fallbackSlug)
              : DEFAULT_PLATFORM_NAME;
        const logoUrl =
          typeof organization["logo_url"] === "string" &&
          organization["logo_url"].trim().length > 0
            ? organization["logo_url"].trim()
            : null;

        if (!isActive) return;
        setBranding({
          orgName: name,
          orgSlug:
            typeof organization.slug === "string" && organization.slug.trim().length > 0
              ? organization.slug.trim()
              : fallbackSlug,
          logoUrl,
          source: "tenant",
        });
      } catch (error) {
        if (error instanceof ResolveOrgRateLimitError) return;
        // Keep slug fallback branding.
      }
    };

    void resolveBranding();
    return () => {
      isActive = false;
    };
  }, []);

  const handleOverride = () => {
    const ttlMinutes = MODERN_BROWSER_POLICY.overrideTtlMinutes ?? 480;
    const expiresAt = new Date(Date.now() + ttlMinutes * 60_000).toISOString();

    try {
      const record: BrowserGateStorageRecord = {
        browserKey: browserPolicyKey,
        acknowledgedAt: new Date().toISOString(),
        expiresAt,
      };
      window.sessionStorage.setItem(
        MODERN_BROWSER_OVERRIDE_KEY,
        JSON.stringify(record),
      );
    } catch {
      // Ignore storage failures and still unlock this session.
    }
    setOverrideEnabled(true);
    void trackLoginFunnelEvent({
      stage: "browser_gate_override",
      policy_state: evaluation.state,
      policy_version: MODERN_BROWSER_POLICY.policyVersion,
      override_expires_at: expiresAt,
      override_ttl_minutes: ttlMinutes,
      warning_acknowledged: true,
    });
  };

  const handleWarningContinue = () => {
    try {
      const record: BrowserGateStorageRecord = {
        browserKey: browserPolicyKey,
        acknowledgedAt: new Date().toISOString(),
      };
      window.sessionStorage.setItem(
        MODERN_BROWSER_WARNING_ACK_KEY,
        JSON.stringify(record),
      );
    } catch {
      // Ignore storage failures and continue for this session.
    }
    setWarningAcknowledged(true);
    void trackLoginFunnelEvent({
      stage: "browser_gate_warning_continue",
      policy_state: evaluation.state,
      policy_version: MODERN_BROWSER_POLICY.policyVersion,
      warning_acknowledged: true,
    });
  };

  if (
    evaluation.state === "supported" ||
    (evaluation.state === "warning" && warningAcknowledged) ||
    (evaluation.state === "blocked" && overrideEnabled)
  ) {
    return <>{children}</>;
  }

  return (
    <BrowserSupportGate
      browser={browser}
      evaluation={evaluation}
      branding={branding}
      supportedFamilies={MODERN_BROWSER_POLICY.allowedFamilies}
      onContinue={evaluation.state === "warning" ? handleWarningContinue : undefined}
      onOverride={handleOverride}
      allowOverride={evaluation.state === "blocked"}
      overrideTtlMinutes={MODERN_BROWSER_POLICY.overrideTtlMinutes}
    />
  );
}
